import {Component, OnInit} from '@angular/core';
import {forkJoin} from 'rxjs';
import {AnimalShelterModel} from '../../../shared/models/animal-shelter.model';
import {ToastService} from '../../../shared/services/toast.service';
import {AnimalSheltersRepository} from "../../../shared/states/animal-shelter.repository";
import {TranslocoService} from "@jsverse/transloco";
import {User} from "../../user/models/user.model";
import {UserRepository} from "../../user/states/user.repository";

@Component({
  selector: 'shelter-overview',
  templateUrl: './shelter-overview.component.html',
  standalone: false,
})
export class ShelterOverviewComponent implements OnInit {
  shelters: Partial<AnimalShelterModel>[] = [];
  displayDialog: boolean = false;
  selectedShelter: Partial<AnimalShelterModel> = this.getEmptyShelter();
  isEditMode: boolean = false;

  // User management
  displayUsersDialog: boolean = false;
  selectedShelterForUsers: Partial<AnimalShelterModel> | null = null;
  shelterUserIds: number[] = [];
  allUsers: User[] = [];
  selectedNewUser: User | null = null;
  loadingUsers: boolean = false;
  loadingAllUsers: boolean = false;

  constructor(
    private readonly _animalShelterRepository: AnimalSheltersRepository,
    private readonly _userRepository: UserRepository,
    private readonly _toastService: ToastService,
    private readonly _translocoService: TranslocoService
  ) {
  }

  ngOnInit() {
    this.loadShelters();
  }

  get shelterUsers(): User[] {
    const assignedIds = new Set(this.shelterUserIds);
    return this.allUsers.filter(u => assignedIds.has(u.id));
  }

  get availableUsers(): User[] {
    const assignedIds = new Set(this.shelterUserIds);
    return this.allUsers.filter(u => !assignedIds.has(u.id));
  }

  loadShelters() {
    this._animalShelterRepository.loadAnimalShelters();
    this._animalShelterRepository.getAllShelters().subscribe({
      next: (shelters) => {
        this.shelters = shelters || [];
      },
      error: (_error) => {
        this._toastService.showError(this._translocoService.translate('animalShelter.error.loadShelters'));
      }
    });
  }

  openNewShelterDialog() {
    this.selectedShelter = this.getEmptyShelter();
    this.isEditMode = false;
    this.displayDialog = true;
  }

  editShelter(shelter: AnimalShelterModel) {
    this.selectedShelter = {...shelter};
    this.isEditMode = true;
    this.displayDialog = true;
  }

  saveShelter() {
    if (this.isEditMode && this.selectedShelter.id) {
      this._animalShelterRepository.updateShelter(this.selectedShelter.id, this.selectedShelter).subscribe({
        next: () => {
          this._toastService.showSuccess('Tierheim erfolgreich aktualisiert');
          this.loadShelters();
          this.hideDialog();
        },
        error: () => {
          this._toastService.showError('Fehler beim Aktualisieren des Tierheims');
        }
      });
    } else {
      this._animalShelterRepository.createShelter(this.selectedShelter).subscribe({
        next: () => {
          this._toastService.showSuccess('Tierheim erfolgreich erstellt');
          this.loadShelters();
          this.hideDialog();
        },
        error: () => {
          this._toastService.showError('Fehler beim Erstellen des Tierheims');
        }
      });
    }
  }

  hideDialog() {
    this.displayDialog = false;
    this.selectedShelter = this.getEmptyShelter();
  }

  // User management methods
  openUsersDialog(shelter: Partial<AnimalShelterModel>) {
    this.selectedShelterForUsers = shelter;
    this.selectedNewUser = null;
    this.displayUsersDialog = true;
    this.loadUsers();
  }

  loadUsers() {
    if (!this.selectedShelterForUsers?.id) {
      return;
    }
    this.loadingUsers = true;
    this.loadingAllUsers = true;
    forkJoin({
      allUsers: this._userRepository.loadAllUsers(),
      userIds: this._animalShelterRepository.getShelterUsers(this.selectedShelterForUsers.id)
    }).subscribe({
      next: ({allUsers, userIds}) => {
        this.allUsers = allUsers || [];
        this.shelterUserIds = userIds || [];
        this.loadingUsers = false;
        this.loadingAllUsers = false;
      },
      error: () => {
        this.loadingUsers = false;
        this.loadingAllUsers = false;
      }
    });
  }

  loadShelterUsers() {
    if (!this.selectedShelterForUsers?.id) {
      return;
    }
    this.loadingUsers = true;
    this._animalShelterRepository.getShelterUsers(this.selectedShelterForUsers.id).subscribe({
      next: (userIds) => {
        this.shelterUserIds = userIds || [];
        this.loadingUsers = false;
      },
      error: () => {
        this.shelterUserIds = [];
        this.loadingUsers = false;
      }
    });
  }

  addUserToShelter() {
    if (!this.selectedShelterForUsers?.id || !this.selectedNewUser) {
      return;
    }
    const boardMembers = [...this.shelterUserIds, this.selectedNewUser.id];
    this._animalShelterRepository.updateShelterUsers(this.selectedShelterForUsers.id, boardMembers).subscribe({
      next: () => {
        this._toastService.showSuccess('Benutzer erfolgreich hinzugefügt');
        this.selectedNewUser = null;
        this.loadShelterUsers();
      },
      error: () => {
        this._toastService.showError('Fehler beim Hinzufügen des Benutzers');
      }
    });
  }

  removeUserFromShelter(userId: number) {
    if (!this.selectedShelterForUsers?.id) {
      return;
    }
    const boardMembers = this.shelterUserIds.filter(id => id !== userId);
    this._animalShelterRepository.updateShelterUsers(this.selectedShelterForUsers.id, boardMembers).subscribe({
      next: () => {
        this._toastService.showSuccess('Benutzer erfolgreich entfernt');
        this.loadShelterUsers();
      },
      error: () => {
        this._toastService.showError('Fehler beim Entfernen des Benutzers');
      }
    });
  }

  getUserDisplayLabel(user: User): string {
    return user.displayName ? `${user.displayName} (${user.name})` : user.name;
  }

  hideUsersDialog() {
    this.displayUsersDialog = false;
    this.selectedShelterForUsers = null;
    this.shelterUserIds = [];
    this.selectedNewUser = null;
  }

  private getEmptyShelter(): Partial<AnimalShelterModel> {
    return {
      title: '',
      address: '',
      zipCode: '',
      city: '',
      phoneNumber: '',
      mailAddress: '',
      boardMembers: []
    };
  }
}
